"use client";

import React, { useRef } from "react";
import { serverReturnType } from "@/components/PagePiller/interfaces";
import { useAppSelector } from "@/redux/hooks";
import PillarSelector from "@/components/PagePiller/redux/selectors/pillarData";
import Breadcrumbs from "@/components/Breadcrumbs";
import routes from "@/helpers/router";
import { COMMON_COMPONENT_TYPES } from "@/helpers/constants";
import componentMapping from "@/components/PagePiller/componentMapping";
import NavBar from "@/components/Header/NavBar";
import { DeregisterRefType, RefsObjectType, RegisterRefType } from "@/components/PageHome/interfaces";
import { useSyncPillarData } from "@/components/PagePiller/useSyncPillarData";
import styles from "./PagePillar.module.scss";

interface PillarPageProps {
  pageInfo: serverReturnType;
  pillarSlug: string;
}

const PillarPage: React.FC<PillarPageProps> = ({ pageInfo, pillarSlug }) => {
  const pillarInfo = pageInfo?.pillarData;
  const userAgent = pageInfo?.ua;

  const elementRefs = useRef<RefsObjectType>({});

  const registerRef: RegisterRefType = (key, element) => {
    elementRefs.current[key] = element;
  };

  // Function to deregister the ref (cleanup)
  const deregisterRef: DeregisterRefType = (key) => {
    delete elementRefs.current[key]; // Deregister the ref when the child unmounts
  };

  // useSyncPillarData({ pillarInfo, pillarSlug });

  // const pillarData = useAppSelector(PillarSelector);
  const category = pillarInfo?.category;

  const extras = {
    userOnBoarded: pageInfo?.userOnBoarded,
    userAgent,
    categorySlug: pillarInfo?.category?.slug,
    pillarSlug: pillarInfo?.slug,
    registerRef,
    deregisterRef,
    elementRefs,
    productInfo: pageInfo?.productInfo,
  };

  // Add ref ID to all content
  const pillarContent = pillarInfo?.content?.map((section, index: number) => ({ ...section, refId: `${index}` }));

  const topPanelSections = pillarContent?.filter((section: any) => section?.Position === "Top");
  const rightPanelSections = pillarContent?.filter(
    (section: any) => section?.Position !== "Left" && section?.Position !== "Top" && section?.Position !== "Bottom"
  );
  const bottomPanelSections = pillarContent?.filter((section: any) => section?.Position === "Bottom");
  const leftPanelSections = pillarContent?.filter((section: any) => section?.Position === "Left");

  const breadCrumbsArray = [
    { label: category?.name, url: routes.category(category?.slug) },
    { label: pillarInfo?.name, url: routes.pillar(pillarInfo?.slug) },
  ];

  const navBarInfo = pillarContent?.find((section) => section.ComponentType === COMMON_COMPONENT_TYPES.NavBar);

  extras["indexContent"] = pillarContent?.filter((section: any) => section?.ShowHeadingInIndex);

  if (userAgent?.isMobile) {
    return (
      <>
        <NavBar
          navBarData={navBarInfo}
          userOnBoarded={pageInfo?.userOnBoarded}
          userAgent={userAgent}
          pathname={routes.pillar(pillarSlug)}
        />
        <div className={`container breadcrumb`}>
          <Breadcrumbs options={breadCrumbsArray} />
        </div>
        {pillarContent?.map((data: any, index: number) => {
          return <React.Fragment key={index}>{componentMapping({ data, extras })}</React.Fragment>;
        })}
      </>
    );
  }

  return (
    <>
      {userAgent.isDesktop && (
        <>
          <NavBar
            navBarData={navBarInfo}
            userOnBoarded={pageInfo?.userOnBoarded}
            userAgent={userAgent}
            pathname={routes.pillar(pillarSlug)}
          />
          <div className={`container breadcrumb`}>
            <Breadcrumbs options={breadCrumbsArray} />
          </div>
          {topPanelSections?.map((data: any, index: number) => {
            return <React.Fragment key={`top_${index}`}>{componentMapping({ data, extras })}</React.Fragment>;
          })}
          <div className={`${styles.columnsContainer} container `}>
            <div className={styles.leftSection}>
              {leftPanelSections?.map((data: any, index: number) => {
                return <React.Fragment key={`left_${index}`}>{componentMapping({ data, extras })}</React.Fragment>;
              })}
            </div>
            <div className={styles.rightSection}>
              {rightPanelSections?.map((data: any, index: number) => {
                return <React.Fragment key={`right_${index}`}>{componentMapping({ data, extras })}</React.Fragment>;
              })}
            </div>
          </div>
          <div>
            {bottomPanelSections?.map((data: any, index: number) => {
              return <React.Fragment key={`bottom_${index}`}>{componentMapping({ data, extras })}</React.Fragment>;
            })}
          </div>
        </>
      )}
    </>
  );
};

export default PillarPage;
